import React, { useState, useEffect } from 'react';
import { Package, Search, ChevronLeft, ChevronRight, CheckCircle2, Clock } from 'lucide-react';
import { adminStaffService } from '../../services/adminStaffService';
import { toast } from 'react-toastify';
import { ORDER_STATUS, ORDER_STATUS_CONFIG } from '../../utils/constants';

const AdminOrderList = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterDate, setFilterDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const data = await adminStaffService.getAllOrders();
            setOrders(data || []);
        } catch (error) {
            toast.error("Không thể tải danh sách đơn hàng!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const handleNewOrder = () => fetchOrders();
        window.addEventListener('newOrderPlaced', handleNewOrder);
        return () => window.removeEventListener('newOrderPlaced', handleNewOrder);
    }, []);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatus, filterDate]);

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price || 0);

    // 👉 ĐÃ SỬA: Bộ lọc ngày nâng cao (Xử lý cả định dạng ISO và định dạng vi-VN)
    const filteredOrders = orders.filter(order => {
        const matchSearch = order.orderCustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            order.orderId?.toString().includes(searchTerm);
        
        const matchStatus = filterStatus === 'all' || order.orderStatus === parseInt(filterStatus);
        
        const matchDate = filterDate === '' || (order.orderDate && (() => {
            // filterDate trả về dạng "YYYY-MM-DD"
            const [year, month, day] = filterDate.split('-');
            const vnFormatDate = `${day}/${month}/${year}`; // Đảo lại thành DD/MM/YYYY
            
            return order.orderDate.includes(filterDate) || order.orderDate.includes(vnFormatDate);
        })());

        return matchSearch && matchStatus && matchDate;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage) || 1;

    const getStatusBadge = (status) => {
        const config = ORDER_STATUS_CONFIG[status];
        if (!config) return <span>Lỗi</span>;
        
        const Icon = config.Icon;
        return (
            <span className={`flex items-center gap-1 ${config.color} ${config.bg} px-2 py-1 rounded text-xs font-bold w-fit`}>
                {Icon && <Icon size={14} />} {config.text}
            </span>
        );
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getPaginationRange = () => {
        const delta = 2; 
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }

        if (currentPage - delta > 2) range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");

        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);

        return range;
    };

    // 👉 ĐÃ SỬA: Format thời gian đơn giản và an toàn (Bỏ nối chuỗi 'Z' do Backend đã chuẩn giờ)
    const formatToVietnamTime = (dateString) => {
        if (!dateString) return '';
        
        // Nếu Backend đã dùng @JsonFormat trả ra chuỗi text ngày tháng vn, in thẳng ra luôn
        if (dateString.includes('/')) return dateString;
        
        // Nếu Backend trả chuẩn ISO 8601 (Kèm múi giờ +07:00), format lại
        const date = new Date(dateString);
        return `${date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} ${date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
    };

    return (
        <div className="font-inter p-4 sm:p-6 lg:p-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Package size={24} className="text-tnc-orange" /> Quản lý Đơn hàng
                </h2>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-4 items-center flex-1">
                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input 
                            type="text" 
                            placeholder="Tìm mã đơn hoặc tên khách..." 
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-tnc-orange text-sm font-medium transition-colors" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                        />
                    </div>
                    
                    <select 
                        className="border border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-tnc-orange text-sm font-medium transition-colors cursor-pointer" 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="all">Tất cả trạng thái</option>
                        {Object.values(ORDER_STATUS).map((status) => (
                            <option key={status} value={status}>
                                {ORDER_STATUS_CONFIG[status]?.text}
                            </option>
                        ))}
                    </select>
                    
                    <input 
                        type="date" 
                        className="border border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-tnc-orange text-sm font-medium text-gray-600 cursor-pointer transition-colors" 
                        value={filterDate} 
                        onChange={(e) => setFilterDate(e.target.value)} 
                    />
                </div>
                <div className="text-sm font-bold text-gray-500">
                    Tổng: <span className="text-tnc-orange text-lg">{filteredOrders.length}</span> đơn
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse min-w-max">
                        <thead>
                            <tr className="bg-[#002855] text-white text-sm tracking-wide">
                                <th className="px-4 py-4 font-bold">Mã ĐH</th>
                                <th className="px-4 py-4 font-bold">Tài khoản</th>
                                <th className="px-4 py-4 font-bold">Khách hàng</th>
                                <th className="px-4 py-4 font-bold">Thời gian</th>
                                <th className="px-4 py-4 font-bold">Tổng tiền</th>
                                <th className="px-4 py-4 font-bold text-center">Thanh toán</th>
                                <th className="px-4 py-4 font-bold">Trạng thái</th>
                                <th className="px-4 py-4 font-bold text-center">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm align-middle bg-white">
                            {loading ? <tr><td colSpan="8" className="text-center py-16 text-gray-500 font-medium">Đang tải dữ liệu...</td></tr> : 
                            currentOrders.length === 0 ? <tr><td colSpan="8" className="text-center py-16 text-gray-500 font-medium">Không tìm thấy đơn hàng nào!</td></tr> :
                            currentOrders.map((order) => {
                                const totalAmount = order.orderPrice + (order.orderShippingFee || 0);
                                return (
                                    <tr key={order.orderId} className="border-b hover:bg-orange-50/50 transition-colors duration-150">
                                        <td className="px-4 py-4">
                                            <span className="font-bold text-tnc-orange text-base">#{order.orderId}</span>
                                        </td>
                                        <td className="px-4 py-4 text-gray-600">
                                            {order.account?.username ? <span className="font-bold text-[#002855]">@{order.account.username}</span> : <span className="text-gray-400 italic text-xs font-medium">Khách vãng lai</span>}
                                        </td>
                                        <td className="px-4 py-4">
                                            <p className="font-bold text-gray-800">{order.orderCustomerName}</p>
                                            <p className="text-xs text-gray-500 mt-1 font-medium">{order.orderPhone}</p>
                                        </td>
                                        <td className="px-4 py-4 text-xs font-semibold text-gray-600">{formatToVietnamTime(order.orderDate)}</td>
                                        <td className="px-4 py-4"><span className="font-bold text-tnc-orange text-base">{formatPrice(totalAmount)}</span></td>
                                        
                                        <td className="px-4 py-4 text-center">
                                            {order.isPaid ? (
                                                <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2.5 py-1 rounded-md text-[11px] font-bold"><CheckCircle2 size={14}/> Đã trả</span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-100 px-2.5 py-1 rounded-md text-[11px] font-bold"><Clock size={14}/> Chưa trả</span>
                                            )}
                                        </td>

                                        <td className="px-4 py-4">{getStatusBadge(order.orderStatus)}</td>
                                        
                                        <td className="px-4 py-4 text-center">
                                            <a 
                                                href={`/admin/orders/${order.orderId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-3.5 py-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md font-bold transition-colors text-xs inline-block shadow-sm"
                                            >
                                                Xem chi tiết
                                            </a>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex flex-col md:flex-row items-center justify-between p-4 border-t border-gray-100 bg-gray-50/50 gap-4">
                        <span className="text-sm text-gray-500 font-medium">
                            Hiển thị <strong className="text-gray-900">{indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredOrders.length)}</strong> trên tổng số <strong className="text-gray-900">{filteredOrders.length}</strong> đơn hàng
                        </span>
                        
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex gap-1.5">
                                <button 
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                
                                {getPaginationRange().map((page, index) => {
                                    if (page === "...") {
                                        return (
                                            <div key={`dots-${index}`} className="w-9 h-9 flex items-center justify-center text-gray-400 font-bold">
                                                ...
                                            </div>
                                        );
                                    }
                                    return (
                                        <button 
                                            key={page} 
                                            onClick={() => handlePageChange(page)}
                                            className={`w-9 h-9 rounded-lg font-bold text-sm transition-colors border shadow-sm ${
                                                currentPage === page 
                                                    ? 'bg-tnc-orange text-white border-tnc-orange' 
                                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-tnc-orange'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button 
                                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>

                            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
                                <span className="text-sm text-gray-500 font-bold">Đến trang:</span>
                                <input 
                                    key={`order-${currentPage}`}
                                    type="number" min={1} max={totalPages} defaultValue={currentPage}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const page = parseInt(e.target.value);
                                            if (page >= 1 && page <= totalPages) handlePageChange(page);
                                            else e.target.value = currentPage;
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const page = parseInt(e.target.value);
                                        if (page >= 1 && page <= totalPages && page !== currentPage) handlePageChange(page);
                                        else e.target.value = currentPage;
                                    }}
                                    className="w-16 h-9 px-2 text-center border border-gray-300 rounded-lg outline-none text-sm font-bold focus:border-tnc-orange focus:ring-1 focus:ring-tnc-orange shadow-sm transition-all"
                                    title="Nhập số trang và nhấn Enter"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminOrderList;